"use client";

import { useCallback, useEffect, useRef } from "react";

const HISTORY_STATE_KEY = "__closeOnHistoryBack";

/**
 * モーダル表示中、端末・ブラウザの戻る操作を「閉じる」として扱う。
 *
 * - 開いたときに同じ URL の履歴を 1 つ積み、戻る操作（popstate）が来たら `onBack` を呼ぶ（画面は移らない）
 * - ボタン等で閉じるときは返り値の関数に後続処理を渡す。積んだ履歴を戻し終えてから呼ぶため、
 *   後続の画面遷移（router.push）が戻る操作と入れ替わらない
 */
export function useCloseOnHistoryBack(
  opened: boolean,
  onBack: () => void,
): (afterClose: () => void) => void {
  const pushedRef = useRef(false);
  const pendingAfterCloseRef = useRef<(() => void) | null>(null);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!opened || typeof window === "undefined") return;

    // StrictMode の再実行などで二重に積まない
    if (!window.history.state?.[HISTORY_STATE_KEY]) {
      window.history.pushState(
        { ...window.history.state, [HISTORY_STATE_KEY]: true },
        "",
      );
    }
    pushedRef.current = true;

    const handlePopState = () => {
      if (!pushedRef.current) return;
      pushedRef.current = false;
      const afterClose = pendingAfterCloseRef.current;
      pendingAfterCloseRef.current = null;
      if (afterClose) {
        afterClose();
      } else {
        onBackRef.current();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [opened]);

  return useCallback((afterClose: () => void) => {
    if (!pushedRef.current || typeof window === "undefined") {
      afterClose();
      return;
    }
    pendingAfterCloseRef.current = afterClose;
    window.history.back();
  }, []);
}
